import React, {
	Fragment,
	Suspense,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { FormattedMessage, useIntl } from 'react-intl-next';

import { SmartCardProvider } from '@atlaskit/link-provider';
import { fg } from '@atlaskit/platform-feature-flags';
import { encodeAdf } from '@atlassian/conversation-assistant-content-transformer/adf-encoder';
import { getFrontendExperiment } from '@atlassian/search-experiment';

import { CardTemplate } from '../../../../constants';
import { messages } from '../../../../messages';
import { useAIAnswerContext } from '../../../ai-answer-context';
import {
	getSearchAIAnalyticsContextData,
	onAIAnswerReturnLinkClick,
	SearchAIAnalyticsContextLocation,
} from '../../../analytics';
import { SearchAIAnalyticsContext } from '../../../search-ai-analytics-context';
import { NLPSearchResultFormat } from '../../../types';
import {
	AIAnswerCloseLabelWrapper,
	AIAnswerResultBlurSection,
	AIAnswerResultBlurSectionText,
	AIAnswerResultSection,
} from '../styled';

import { StatusCitation } from './status-citation';

const ReactRenderer = React.lazy(() =>
	import(/* webpackChunkName: "@atlaskit-internal_.renderer" */ '@atlaskit/renderer').then(
		({ ReactRenderer }) => {
			return {
				default: ReactRenderer,
			};
		},
	),
);

const MarkdownRenderer = React.lazy(() =>
	import(/* webpackChunkName: "react-markdown" */ 'react-markdown').then(
		({ default: ReactMarkdown }) => {
			return {
				default: ReactMarkdown,
			};
		},
	),
);

export const RenderedResultComponent = (): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [isResultOverflow, setResultOverflow] = useState<boolean>(false);
	const [isResultExpanded, setResultExpanded] = useState<boolean>(false);
	const wrapperRef = useRef<HTMLDivElement | null>(null);
	const contentRef = useRef<HTMLDivElement | null>(null);
	const { commonAttributes, fireAnalyticsEvent, isReadingAids, cardTemplate, onClose } =
		useAIAnswerContext();
	const { answerString, answerFormat } = commonAttributes;
	const onCloseDialog = useCallback(() => {
		fireAnalyticsEvent(onAIAnswerReturnLinkClick(commonAttributes));
		onClose?.();
	}, [commonAttributes, fireAnalyticsEvent, onClose]);

	const { serveExperiment: serveDefinitionsUXExp } = getFrontendExperiment({
		abTestId: 'kd_definitions_ux_uplift_exp',
	});
	const serveDefinitionUXUpdates = serveDefinitionsUXExp && fg('kd_definitions_ux_uplift_exp_gate');

	const enableShowMoreBlur =
		cardTemplate === CardTemplate.DEFAULT && isReadingAids && !serveDefinitionUXUpdates;

	useEffect(() => {
		if (!wrapperRef.current || !contentRef.current) {
			return;
		}

		if (serveDefinitionUXUpdates) {
			return;
		}

		const resizeObserver = new ResizeObserver(() => {
			if ((wrapperRef.current?.offsetHeight ?? 0) < (contentRef.current?.offsetHeight ?? 0)) {
				setResultOverflow(true);
			} else {
				setResultOverflow(false);
			}
		});

		resizeObserver.observe(contentRef.current);
		return () => resizeObserver.disconnect();
	}, [serveDefinitionUXUpdates]);

	const docContent = useMemo(() => encodeAdf(answerString, false), [answerString]);

	const FormattedResult = useMemo(() => {
		let content;
		switch (answerFormat) {
			case NLPSearchResultFormat.ADF:
				content = (
					<ReactRenderer
						document={JSON.parse(answerString)}
						nodeComponents={
							fg('hybrid_orchestrator_enable_image_search')
								? undefined
								: {
										// Preventing media and mediaSingle from rendering images due to security vulnerability: https://asecurityteam.atlassian.net/browse/VULN-1543153
										// Followup ticket to re-enable image rendering: https://hello.jira.atlassian.cloud/browse/CCSEARCH-4799
										media: () => <Fragment />,
										mediaSingle: () => <Fragment />,
									}
						}
					/>
				);
				break;
			case NLPSearchResultFormat.MARKDOWN:
				if (!isReadingAids) {
					content = (
						<ReactRenderer
							document={docContent}
							nodeComponents={{
								status: (props) => <StatusCitation {...props} />,
								// Preventing media and mediaSingle from rendering images due to security vulnerability: https://asecurityteam.atlassian.net/browse/VULN-1543153
								// Followup ticket to re-enable img rendering: https://hello.jira.atlassian.cloud/browse/CCSEARCH-4799
								...(!fg('hybrid_orchestrator_enable_image_search')
									? {
											media: () => <Fragment />,
											mediaSingle: () => <Fragment />,
										}
									: undefined),
							}}
						/>
					);
					break;
				}
				// Preventing img elements from rendering due to security vulnerability: https://asecurityteam.atlassian.net/browse/VULN-1543153
				// Followup ticket to re-enable img rendering: https://hello.jira.atlassian.cloud/browse/CCSEARCH-4799
				content = (
					<MarkdownRenderer
						disallowedElements={fg('hybrid_orchestrator_enable_image_search') ? undefined : ['img']}
					>
						{answerString}
					</MarkdownRenderer>
				);
				break;
			default:
				return <Fragment />;
		}
		return content;
	}, [answerFormat, answerString, docContent, isReadingAids]);

	return (
		<Fragment>
			<AIAnswerResultSection
				id="ai-answer-result-section"
				ref={wrapperRef}
				isReadingAids={isReadingAids && cardTemplate === CardTemplate.DEFAULT}
				isExpanded={isResultExpanded || serveDefinitionUXUpdates}
				serveDefinitionUXExp={serveDefinitionUXUpdates}
			>
				<div ref={contentRef}>
					<Suspense fallback={<Fragment />}>
						<SearchAIAnalyticsContext
							data={getSearchAIAnalyticsContextData({
								location: SearchAIAnalyticsContextLocation.SAIN_Q_AND_A_CARD,
								commonAttributes,
							})}
						>
							<SmartCardProvider>{FormattedResult}</SmartCardProvider>
						</SearchAIAnalyticsContext>
					</Suspense>
				</div>
			</AIAnswerResultSection>
			{enableShowMoreBlur && isResultOverflow && !isResultExpanded && (
				<AIAnswerResultBlurSection onClick={() => setResultExpanded(true)}>
					<AIAnswerResultBlurSectionText>
						{formatMessage(messages.ai_answer_expand_button_label)}
					</AIAnswerResultBlurSectionText>
				</AIAnswerResultBlurSection>
			)}
			{!isReadingAids && onClose && (
				<AIAnswerCloseLabelWrapper>
					<FormattedMessage
						{...messages.ai_answer_dialog_cancel_label}
						values={{
							searchResultsLink: (
								// eslint-disable-next-line @atlaskit/design-system/no-html-anchor, @atlassian/a11y/anchor-is-valid, @atlassian/a11y/click-events-have-key-events, @atlassian/a11y/no-static-element-interactions
								<a onClick={onCloseDialog}>
									{formatMessage(messages.ai_answer_dialog_search_results_label)}
								</a>
							),
						}}
					/>
				</AIAnswerCloseLabelWrapper>
			)}
		</Fragment>
	);
};
